import type { Camera, Matrix4 } from "three";

export const POST_PROCESSING_CAMERA_REST_MS = 120;

function sameMatrix(left: Matrix4, right: Matrix4): boolean {
  return left.elements.every(
    (value, index) => Math.abs(value - (right.elements[index] ?? 0)) <= 1e-6,
  );
}

/**
 * Tracks motion without React updates or a continuously invalidated demand loop.
 *
 * Only real camera motion makes the camera active. `held` (the user is
 * controlling the camera) keeps an active camera from resting, however long a
 * slow orbit pauses; the rest window starts again when the hold ends. A hold
 * without motion, like a click, never activates anything.
 */
export function createPostProcessingCameraActivity(
  camera: Camera,
  invalidate: () => void,
) {
  camera.updateWorldMatrix(true, false);
  const world = camera.matrixWorld.clone();
  const projection = camera.projectionMatrix.clone();
  let active = false;
  let held = false;
  let lastMotion = 0;
  let timeout: ReturnType<typeof setTimeout> | undefined;

  const armRest = () => {
    timeout ??= setTimeout(finishWhenRested, POST_PROCESSING_CAMERA_REST_MS);
  };

  function finishWhenRested() {
    timeout = undefined;
    // The release re-arms the rest; a timer from before the hold must not end it.
    if (!active || held) return;
    const remaining =
      POST_PROCESSING_CAMERA_REST_MS - (performance.now() - lastMotion);
    if (remaining > 0) {
      timeout = setTimeout(finishWhenRested, remaining);
    } else {
      active = false;
      invalidate();
    }
  }

  return {
    update(holding = false): boolean {
      camera.updateWorldMatrix(true, false);
      const released = held && !holding;
      held = holding;
      if (
        !sameMatrix(world, camera.matrixWorld) ||
        !sameMatrix(projection, camera.projectionMatrix)
      ) {
        world.copy(camera.matrixWorld);
        projection.copy(camera.projectionMatrix);
        lastMotion = performance.now();
        active = true;
        armRest();
      } else if (released && active) {
        lastMotion = performance.now();
        armRest();
      }
      return active;
    },
    reset(): void {
      if (timeout !== undefined) clearTimeout(timeout);
      timeout = undefined;
      active = false;
      held = false;
      camera.updateWorldMatrix(true, false);
      world.copy(camera.matrixWorld);
      projection.copy(camera.projectionMatrix);
    },
  };
}
